import { FormInstance } from 'antd';
import { useCallback, useState } from 'react';

import {
  testIsAddressSuspect,
  testIsNameSuspect,
} from '../utils/customerDataFormUtils';

import { CustomerDataModel } from '~/core/models';

const useFormFieldConfirmations = (form: FormInstance<CustomerDataModel>) => {
  const [showConfirmName, setShowConfirmName] = useState(false);
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [showConfirmAddress, setShowConfirmAddress] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);

  const handleInputBlur = useCallback(
    async (_e: React.FocusEvent<HTMLFormElement> | undefined) => {
      // force all field validation so the errors will be updated
      // eslint-disable-next-line arrow-body-style
      await form.validateFields({ validateOnly: true }).catch(() => {
        // ignore errors
        return;
      });

      const formData = form.getFieldsValue() as CustomerDataModel;
      const { customerName, address1 } = formData;

      const errorsAndWarnings = form.getFieldsError();
      const errors = errorsAndWarnings
        .filter((value) => value.errors.length > 0)
        .map((value) => value.name[0]);
      const hasErrors = errors.length > 0;

      // if the name is suspect, enable the showConfirmName
      const isNameSuspect = testIsNameSuspect(customerName);

      setShowConfirmName(
        !!(customerName?.trim() ?? '').length && isNameSuspect
      );

      // if not suspect, then clear the checkbox in case it is suspect again
      if (!isNameSuspect) {
        setNameConfirmed(false);
      }

      // same behavior for the address as for the name
      const isAddressSuspect = testIsAddressSuspect(
        address1,
        formData.address2,
        formData.city,
        formData.state,
        formData.zip
      );

      setShowConfirmAddress(
        !!(address1?.trim() ?? '').length && isAddressSuspect
      );
      if (!isAddressSuspect) {
        setAddressConfirmed(false);
      }

      if (hasErrors) {
        console.warn('Form has errors', hasErrors);
      }
    },
    [
      form,
      setAddressConfirmed,
      setNameConfirmed,
      setShowConfirmAddress,
      setShowConfirmName,
    ]
  );

  return {
    addressConfirmed,
    handleInputBlur,
    nameConfirmed,
    setAddressConfirmed,
    setNameConfirmed,
    showConfirmAddress,
    showConfirmName,
  };
};

export default useFormFieldConfirmations;
